/**
 * Guest Token Utilities for secure token-based authentication
 * Updated to use RPC v2 CBOR protocol for token validation
 * 
 * This implementation replaces the previous HMAC-based validation
 * with a backend API call that uses AWS KMS for token validation.
 */

// Import CBOR from cbor-js library
const CBOR = require('cbor-js');

export interface ParsedGuestToken {
    caseId: string;
    interviewId: string;
    isValid: boolean;
    isExpired: boolean;
    errorMessage?: string;
}

/**
 * Guest Token Validator with RPC v2 CBOR protocol support
 * Handles token validation using the RPC v2 CBOR protocol
 */
class GuestTokenValidator {
    private apiEndpoint: string;
    private validateUrl: string;
    private debugMode: boolean;
    
    constructor(apiEndpoint?: string) {
        // Detect if running locally and use appropriate endpoint
        const isLocalDevelopment = window.location.hostname === 'localhost';
        const isBeta = window.location.hostname?.includes('beta');
        
        if (apiEndpoint) {
            this.apiEndpoint = apiEndpoint;
        } else if (isLocalDevelopment) {
            // For local development, use the current origin to avoid CORS issues
            // This assumes a development proxy is configured to route /api/* to the backend
            this.apiEndpoint = window.location.origin;
        } else if (isBeta) {
            // For local development, use the current origin to avoid CORS issues
            // This assumes a development proxy is configured to route /api/* to the backend
            this.apiEndpoint = 'https://consent.beta.hrsc.pxt.amazon.dev';
        } else {
            // For production, use the CloudFront domain
            this.apiEndpoint = 'https://consent.hrsc.pxt.amazon.dev';
        }
        
        this.validateUrl = `${this.apiEndpoint}/api/hrxInvestigationConsentBackend/service/HRxInvestigationConsentBackend/operation/ValidateGuestToken`;
        this.debugMode = new URLSearchParams(window.location.search).has('debug');
    }

    /**
     * Log debug information if debug mode is enabled
     */
    private log(...args: any[]): void {
        if (this.debugMode) {
            console.log('[GuestTokenValidator]', ...args);
        }
    }


    /**
     * Validate guest token using RPC v2 CBOR protocol
     */
    async validateGuestToken(token: string): Promise<any> {
        if (!token || token.trim().length === 0) {
            throw new Error('Token is required');
        }

        const requestData = { token: token };
        
        try {
            // Encode request to CBOR
            const cborData = CBOR.encode(requestData);
            
            console.log('Validating guest token with RPC v2 CBOR protocol...');
            //this.log('Token length:', token.length);

            const response = await fetch(this.validateUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/cbor',
                    'Accept': 'application/cbor',
                    'smithy-protocol': 'rpc-v2-cbor',
                    'X-Amzn-Guest-Token': token
                },
                body: cborData,
            });

            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                
                try {
                    const errorBuffer = await response.arrayBuffer();
                    //const errorData = this.decodeFromCbor(new Uint8Array(errorBuffer));
                    const errorData = CBOR.decode(errorBuffer);
                    errorMessage = errorData.message || errorMessage;
                } catch (decodeError) {
                    try {
                        const textError = await response.text();
                        errorMessage = textError || errorMessage;
                    } catch (textError) {
                        // Use default error message
                    }
                }
                
                throw new Error(errorMessage);
            }

            // Decode CBOR response
            const responseBuffer = await response.arrayBuffer();
            //const result = this.decodeFromCbor(new Uint8Array(responseBuffer));
            const result = CBOR.decode(responseBuffer);
            
            console.log('Token validation successful:', result);
            return result;

        } catch (error) {
            console.error('Token validation failed:', error);
            throw error;
        }
    }
}

/**
 * Guest Token Utilities class - Updated to use RPC v2 CBOR protocol
 */
export class GuestTokenUtils {
    /**
     * Parse and validate a guest token using the RPC v2 CBOR API
     * 
     * @param token - The token to validate
     * @returns Parsed token data with validation results
     */
    public static async parseAndValidateToken(token: string): Promise<ParsedGuestToken> {
        try {
            if (!token || token.trim().length === 0) {
                return {
                    caseId: '',
                    interviewId: '',
                    isValid: false,
                    isExpired: false,
                    errorMessage: 'Token is required'
                };
            }

            // Create validator instance at runtime to allow proper environment detection in tests
            const validator = new GuestTokenValidator();
            
            // Use the RPC v2 CBOR API for validation
            const result = await validator.validateGuestToken(token);
            
            // Extract data from the API response
            const caseId = result.caseId || '';
            const interviewId = result.interviewId || '';
            const isValid = result.valid || false;
            const isExpired = result.isExpired || false;

            return {
                caseId,
                interviewId,
                isValid: isValid && !isExpired,
                isExpired,
                errorMessage: isExpired ? 'Token has expired' : (isValid ? undefined : 'Token validation failed')
            };

        } catch (error) {
            console.error('Token validation error:', error);
            return {
                caseId: '',
                interviewId: '',
                isValid: false,
                isExpired: false,
                errorMessage: error instanceof Error ? error.message : 'Token validation failed'
            };
        }
    }

    /**
     * Extract token from URL query parameters
     * Preserves original token format by avoiding automatic URL decoding of + symbols
     * @param url - The URL to extract token from (defaults to current location)
     * @returns The token string or null if not found
     */
    public static extractTokenFromUrl(url?: string): string | null {
        try {
            const urlToCheck = url || window.location.href;
            
            // Parse the URL to get the query string
            const urlObj = new URL(urlToCheck);
            const queryString = urlObj.search;
            
            if (!queryString) {
                return null;
            }
            
            // Manually parse query parameters to preserve + symbols in tokens
            // Standard URL decoding converts + to spaces, but tokens may contain + as literal characters
            const params = queryString.substring(1).split('&');
            
            for (const param of params) {
                const equalIndex = param.indexOf('=');
                if (equalIndex === -1) continue;
                
                const key = param.substring(0, equalIndex);
                const value = param.substring(equalIndex + 1);
                
                if (!key || !value) continue;
                
                const decodedKey = decodeURIComponent(key);
                
                // Check for token parameter names
                if (decodedKey === 'token') {
                    // Custom decode that preserves + symbols
                    // Replace %XX encoded characters but leave + symbols as-is
                    let decodedValue = value;
                    
                    // Only decode percent-encoded characters, not + symbols
                    decodedValue = decodedValue.replace(/%([0-9A-Fa-f]{2})/g, (match, hex) => {
                        return String.fromCharCode(parseInt(hex, 16));
                    });
                    
                    return decodedValue;
                }
            }
            
            return null;
        } catch (error) {
            console.error('Error extracting token from URL:', error);
            return null;
        }
    }

}
